import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { FormattedMessage, useIntl } from "react-intl";

import Attachment from "@components/Attachment";
import TertiaryButton from "@components/buttons/TertiaryButton";
import DateStamp from "@components/dates/DateStamp";
import HighlightLabel from "@components/labels/HighlightLabel";
import styles from "@components/MarketplaceItem.module.css";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import TextWithIcon from "@components/text/TextWithIcon";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import Heading2 from "@components/typography/Heading2";
import Heading3 from "@components/typography/Heading3";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  EmployerBenefitCategoryEnum,
  EmployerBenefitFragment
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";
import { isValidHttpUrl, isValidPhoneNumber, shortenWebsite } from "@utils/stringUtils";

type Props = {
  employerBenefit: EmployerBenefitFragment;
  journeyId: string | null;
};

const MarketplaceItem = ({ employerBenefit, journeyId }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const myClient = useMyClient();

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    "appEvent"
  );

  const handleClickThrough = (type: string) => {
    if (myClient) {
      createAppEvent({
        actionKind: AppEventActionKindEnum.employer_benefit_click_through,
        context: type,
        journeyId,
        resourceId: employerBenefit.id
      });
    }
  };

  const sanitizedDescription = useSanitizeHtml(employerBenefit.description, journeyId, employerBenefit);

  return (
    <div>
      {journeyId && (
        <div className={styles.flex}>
          <TertiaryButton
            icon="chevronLeft"
            routerLink={journeyRoute({
              journeyId,
              linkAfterJourney: "/employer-benefits",
              myExpert,
              preventScroll: false
            })}
            textKey="pages.client.marketplace.marketplace"
          />
          {myExpert && (
            <BodyCopy>
              <FormattedMessage
                id="pages.client.marketplace.lastUpdated"
                values={{ date: <DateStamp value={employerBenefit.updatedAt} /> }}
              />
            </BodyCopy>
          )}
        </div>
      )}
      <IonGrid className="ion-no-padding">
        <IonRow>
          <IonCol size="3" sizeLg="3" sizeMd="3" sizeSm="12" sizeXl="3" sizeXs="12">
            <HighlightLabel leftPx={18} textKey="pages.client.marketplace.employerSponsored" topPx={-7} />
            {employerBenefit.photoUrl ? (
              <img alt={employerBenefit.vendorName} className={styles.img} src={employerBenefit.photoUrl} />
            ) : (
              <TextWithIcon icon="spinner" iconFirst text={intl.formatMessage({ id: "dictionary.scanningImage" })} />
            )}
            {employerBenefit.links.length > 0 && (
              <div className={styles.itemSpacing}>
                <Heading3>
                  <FormattedMessage id="pages.client.marketplace.website" />
                </Heading3>
                {employerBenefit.links.map(link => (
                  <div className={styles.website} key={link}>
                    <ExternalLink href={link} onClick={() => handleClickThrough("website")}>
                      <Icon icon="globe" spacingEnd />
                      {shortenWebsite(link)}
                    </ExternalLink>
                  </div>
                ))}
              </div>
            )}
            {employerBenefit.phoneNumberLabel && employerBenefit.phoneNumberLink && (
              <div className={styles.itemSpacing}>
                <Heading3>
                  <FormattedMessage id="pages.client.marketplace.phone" />
                </Heading3>
                <ExternalLink
                  href={
                    isValidPhoneNumber(employerBenefit.phoneNumberLink)
                      ? `tel:${employerBenefit.phoneNumberLink}`
                      : employerBenefit.phoneNumberLink
                  }
                  onClick={() => handleClickThrough("phone")}
                >
                  <Icon icon="phone" spacingEnd />
                  {employerBenefit.phoneNumberLabel}
                </ExternalLink>
              </div>
            )}
            {employerBenefit.hoursOfOperation && (
              <div className={styles.itemSpacing}>
                <Heading3>
                  <FormattedMessage id="pages.client.marketplace.hoursOfOperation" />
                </Heading3>
                <BodyCopy>{employerBenefit.hoursOfOperation}</BodyCopy>
              </div>
            )}
            {employerBenefit.emailLabel && employerBenefit.emailLink && (
              <div className={styles.itemSpacing}>
                <Heading3>
                  <FormattedMessage id="pages.client.marketplace.email" />
                </Heading3>
                <ExternalLink
                  href={
                    isValidHttpUrl(employerBenefit.emailLink)
                      ? employerBenefit.emailLink
                      : `mailto: ${employerBenefit.emailLink}`
                  }
                  onClick={() => handleClickThrough("email")}
                >
                  <Icon icon="envelope" spacingEnd />
                  {employerBenefit.emailLabel}
                </ExternalLink>
              </div>
            )}
            {employerBenefit.attachments.length > 0 && (
              <div className={styles.itemSpacing}>
                <Heading3>
                  <FormattedMessage id="pages.client.marketplace.pdfs" />
                </Heading3>
                {employerBenefit.attachments.map(attachment => (
                  <Attachment attachment={attachment} key={attachment.id} />
                ))}
              </div>
            )}
          </IonCol>
          <IonCol size="9" sizeLg="9" sizeMd="9" sizeSm="12" sizeXl="9" sizeXs="12">
            <div className={styles.text}>
              <Heading3 color="primary-500">
                {employerBenefit.categories.map((category, index) => (
                  <span key={category}>
                    {category === EmployerBenefitCategoryEnum.OTHER && employerBenefit.categoryOther ? (
                      employerBenefit.categoryOther
                    ) : (
                      <FormattedMessage id={`models.employerBenefitCategory.${category}`} />
                    )}
                    {index !== employerBenefit.categories.length - 1 && (
                      <>
                        <FormattedMessage id="dictionary.punctuation.comma" />
                        <Nbsp />
                      </>
                    )}
                  </span>
                ))}
              </Heading3>
              <div className={styles.title}>
                <Heading2>{employerBenefit.vendorName}</Heading2>
              </div>
              <div>{sanitizedDescription}</div>
              {employerBenefit.cost && (
                <>
                  <div className={styles.cost}>
                    <Heading2>
                      <FormattedMessage id="pages.client.marketplace.cost" />
                    </Heading2>
                  </div>
                  <div>
                    <BodyCopy>{employerBenefit.cost}</BodyCopy>
                  </div>
                </>
              )}
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
    </div>
  );
};

export default MarketplaceItem;
